import { Component, EventEmitter, Input, Output } from "@angular/core";

import { Observable, BehaviorSubject } from "rxjs";
import { ApicallService, ApicallOption } from "@core-services/apicall.service";
import { Helper } from "@helpers/functions";
import queryString from "query-string";
import { Model } from "@models";
import { empty } from "@helpers/utils";
import { PAGINATION_PAGE_SIZES } from "@helpers/constants";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";

// TODO: eslint fix
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({
	selector: "bp-pagination",
	templateUrl: "./pagination.component.html",
	styleUrls: ["./pagination.component.scss"],
	standalone: false
})
export class PaginationComponent<M extends Model> {
	private _type: typeof Model;
	private _url: string = "";
	private _list: BehaviorSubject<M[]> = new BehaviorSubject<M[]>([]);
	private _isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private _pageNumber: number = 1;
	private _pageSize: number = PAGINATION_PAGE_SIZES[0];
	private _silent: boolean = false;
	private _rawCopy: boolean = false;

	@Input()
	public hide: boolean = false;
	@Output("onLoaded")
	public _pageLoaded: EventEmitter<void> = new EventEmitter<void>();

	private filter = new Map<string, string>();
	private data: any = null;
	private pagination: any = {};
	private stepSize: number = 3;

	public constructor(
		private apicallService: ApicallService,
		private readonly coreStoreFacade: CoreStoreFacade
	) {}

	public setStepSize(stepSize: number): void {
		this.stepSize = stepSize;
	}

	public getFilter(): Map<string, string> {
		return this.filter;
	}

	public getStepSize(): number {
		return this.stepSize;
	}

	public setType(type: any): void {
		this._type = type;
	}

	public setUrl(u: string): void {
		this._url = u;
	}

	public loadPage(page: number): void {
		if (page < 1 || (page > this.last && this.last > 0)) {
			return;
		}
		this._pageNumber = page;
		this.init();
	}

	public loadFirstPage(reload = false): void {
		if (this._pageNumber === 1 && !reload) {
			return;
		}
		this.loadPage(1);
	}

	public loadPrev(): void {
		this.loadPage(this._pageNumber - 1);
	}

	public loadNext(): void {
		this.loadPage(this._pageNumber + 1);
	}

	public loadLastPage(): void {
		if (this._pageNumber === this.last) {
			return;
		}
		this.loadPage(this.last);
	}

	public setPageSize(counts: number): void {
		this._pageSize = counts;
		this.loadFirstPage(true);
	}

	public init(): void {
		this._isLoading$.next(true);
		const option = new ApicallOption(this.type, this.data);
		option.pagination = true;
		option.rawCopy = this.getRawCopy();
		let call: Observable<any[]> = null;

		const pagination: any = {
			"page[number]": this.pageNumber,
			"page[size]": this._pageSize
		};

		this.filter.forEach((value: string, key: string) => (pagination[key] = value));
		const url = this._url + "?" + queryString.stringify(pagination);

		if (this.data === undefined || this.data === null) {
			call = this.apicallService.get(url, option);
		} else {
			call = this.apicallService.post(url, option);
		}

		call.subscribe(
			(data) => {
				this._list.next(data[0]);
				this.pagination = data[1];
				if (this._pageLoaded) {
					this._pageLoaded.next();
				}
			},
			(error) => Helper.handleError(error),
			() => this._isLoading$.next(false)
		);
	}

	public fetchData<T>(url: string, type: T): BehaviorSubject<any[]> {
		this.setUrl(url);
		this.type = type;
		this.init();
		return this._list;
	}

	public unset(): void {
		this._list.next(null);
		this._list.complete();
		this.pagination = {};
	}

	public get pagesCount(): number[] {
		const pages = [];

		let start = this.pageNumber - this.stepSize;
		let end = this.pageNumber + this.stepSize;
		if (start < 1) {
			start = 1;
			end = Math.min(start + this.stepSize * 2, this.last);
		}
		if (end >= this.last) {
			end = this.last;
			start = Math.max(end - this.stepSize * 2, 1);
		}
		while (start <= end) {
			pages.push(start++);
		}
		return pages;
	}

	public get pagesSize(): number[] {
		if (empty(this.pagination) || this.all < PAGINATION_PAGE_SIZES[0]) {
			return [];
		}

		return PAGINATION_PAGE_SIZES.filter((size: number) => this.all >= size);
	}

	public get type(): any {
		return this._type;
	}

	public set type(t: any) {
		this._type = t;
	}

	public get url(): string {
		return this._url;
	}

	public get list(): M[] {
		return this._list.getValue();
	}

	public getList$(): BehaviorSubject<M[]> {
		return this._list;
	}

	public getList(): Observable<M[]> {
		return this._list.asObservable();
	}

	public get pageNumber(): number {
		return this._pageNumber;
	}

	public get pageSize(): number {
		return this._pageSize;
	}

	public getSilent(): boolean {
		return this._silent;
	}

	public setSilent(silent: boolean): void {
		this._silent = silent;
	}

	public get all(): number {
		return this.pagination["total_count"];
	}

	public get last(): number {
		return Math.ceil(this.all / this.pagination["page_size"]);
	}

	public isLoading$(): BehaviorSubject<boolean> {
		return this._isLoading$;
	}

	public setRawCopy(value: boolean): void {
		this._rawCopy = value;
	}

	private getRawCopy(): boolean {
		return this._rawCopy;
	}

	public get pageLoaded(): EventEmitter<any> {
		return this._pageLoaded;
	}

	public set pageLoaded(value: EventEmitter<any>) {
		this._pageLoaded = value;
	}

	public get loading(): boolean {
		return this.coreStoreFacade.coreStoreSnapshot.isApiLoading;
	}

	public unsetFilter(field: string): void {
		if (this.filter.get(field)) {
			this.filter.delete(field);
		}
		this._pageNumber = 1;
		this.init();
	}

	public setFilter(type: string, value: string): void {
		this.filter.set(type, value);
		this._pageNumber = 1;
		this.init();
	}

	public clearFilter(init = true): void {
		if (this.filter.size === 0) {
			return;
		}
		this.filter = new Map<string, string>();
		this._pageNumber = 1;
		if (init) {
			this.init();
		}
	}
}
